import { useMemo } from "react";
import { PaymentElement as StripePaymentElement } from "@stripe/react-stripe-js";
import type { StripePaymentElementOptions } from "@stripe/stripe-js";
import I18n from "@/i18n-js/instance";
import paymentElementImageDesktop from "@circle-assets/images/paywalls/payment-form-desktop.png";
import paymentElementImageMobile from "@circle-assets/images/paywalls/payment-form-mobile.png";
import paymentElementImageTablet from "@circle-assets/images/paywalls/payment-form-tablet.png";
import { usePaywallCheckoutContext } from "@circle-react/contexts/Paywalls/paywallCheckoutContext";
import { Icon } from "@circle-react-shared/Icon";
import { TippyV2 as Tippy } from "@circle-react-shared/TippyV2";
import { Typography } from "@circle-react-uikit/Typography";
import { usePayment } from "../../../hooks/usePayment";
import { CheckoutFormFieldWrapper } from "../CheckoutFormFieldWrapper";
import { usePaymentElementDefaultValues } from "./usePaymentElementDefaultValues";
import { usePaymentElementFields } from "./usePaymentElementFields";
import { usePaymentElementLayout } from "./usePaymentElementLayout";
import { usePaymentElementTerms } from "./usePaymentElementTerms";

export const PaymentElement = () => {
  const { getSelectedPrice, checkoutPreviewContext } =
    usePaywallCheckoutContext();
  const selectedPrice = getSelectedPrice();

  const defaultValues = usePaymentElementDefaultValues();
  const fields = usePaymentElementFields();
  const terms = usePaymentElementTerms();
  const layout = usePaymentElementLayout();

  const options = useMemo<StripePaymentElementOptions>(
    () => ({
      defaultValues,
      fields,
      terms,
      layout,
    }),
    [defaultValues, fields, layout, terms],
  );

  const { handleOnChangeCard, handleOnReadyCard, onLoadError } = usePayment();

  const isCheckoutPreview = !!checkoutPreviewContext;

  return (
    <CheckoutFormFieldWrapper
      name="payment"
      childClassName="!mb-4"
      LabelComponent={Typography.TitleSm}
      addon={
        selectedPrice.trial_enabled && (
          <span className="inline-flex">
            <Tippy
              className="inline-flex"
              content={I18n.t("paywall_checkout.payment_subtitle_tooltip")}
            >
              <Icon type="12-question-mark" size={12} color="text-primary" />
            </Tippy>
          </span>
        )
      }
      data-testid="checkout-form-payment-methods-field-wrapper"
    >
      {isCheckoutPreview && (
        <>
          <img
            src={paymentElementImageDesktop}
            alt=""
            loading="lazy"
            className="hidden lg:block"
          />
          <img
            src={paymentElementImageTablet}
            alt=""
            loading="lazy"
            className="hidden sm:block lg:hidden"
          />
          <img
            src={paymentElementImageMobile}
            alt=""
            loading="lazy"
            className="sm:hidden"
          />
        </>
      )}
      {!isCheckoutPreview && (
        <StripePaymentElement
          options={options}
          onChange={handleOnChangeCard}
          onReady={handleOnReadyCard}
          onLoadError={onLoadError}
        />
      )}
    </CheckoutFormFieldWrapper>
  );
};
